import React, {
  useRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as Yup from 'yup';
import clsx from 'clsx';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import { ServiceProviderFormData } from '@/types/service-provider.types';
import CountryCodeSelect from '@/components/CountryCodeSelect/CountryCodeSelect';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';
import { createServiceProviderValidationSchema } from '@/utils/validations';
import { getDialCodeFromIso, normalizeNationalPhoneNumber } from '@/utils/phone';
import { countryLabels } from '@/constant/countries';
import countries from 'world-countries';
import { englishCountryName, localizedCountryOptions } from '@/utils/countryName';
import { useLanguage } from '@/i18n';
import {
  useCreateServiceProviderMutation,
  useUpdateServiceProviderMutation,
  useGetServiceProviderByIdQuery,
  useGetAvailableHarborsQuery,
} from '@/services/ServiceProviderService';
import { ErrorApiResponse } from '@/types/api.types';
import NumberInput from '@/components/inputControls/NumberInput';
import { useManagedListOptions } from '@/hooks/useManagedListOptions';
import { ManagedListType } from '@/types/list-value.types';

interface ServiceProviderFormPageProps {
  mode: 'create' | 'edit';
}

const ServiceProviderFormPage = ({ mode }: ServiceProviderFormPageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { currentLanguage } = useLanguage();
  const { options: serviceCategories, isLoading: isLoadingServiceCategories } =
    useManagedListOptions(ManagedListType.SERVICE_CATEGORY);

  const isEdit = mode === 'edit';
  const serviceProviderId = isEdit ? params.id : undefined;

  const businessNameRef = useRef<HTMLInputElement>(null);
  const [harborSearchTerm, setHarborSearchTerm] = useState('');
  const [isHarborDropdownOpen, setIsHarborDropdownOpen] = useState(false);
  const harborDropdownRef = useRef<HTMLDivElement>(null);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState<Partial<ServiceProviderFormData>>(
    {}
  );
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: harborsData, isLoading: isLoadingHarbors } =
    useGetAvailableHarborsQuery({
      showharborlisting: true,
    });

  const availableHarbors = useMemo(
    () => harborsData?.data || [],
    [harborsData]
  );

  const { data: serviceProviderDetail, isLoading: isDetailLoading } =
    useGetServiceProviderByIdQuery(serviceProviderId!, {
      skip: !isEdit || !serviceProviderId,
    });

  useEffect(() => {
    if (serviceProviderDetail?.data && isEdit) {
      const detail = serviceProviderDetail.data;
      setFormData({
        id: detail.id,
        legal_name: detail.legal_name,
        display_name: detail.display_name,
        vat_id: detail.vat_id,
        contact_email: detail.contact_email,
        contact_phone: detail.contact_phone
          ? normalizeNationalPhoneNumber(
              detail.contact_phone,
              detail.contact_phone_country_short_code || detail.country_short_code
            )
          : '',
        country_code:
          getDialCodeFromIso(
            detail.contact_phone_country_short_code,
            countryLabels
          ) || detail.country_code,
        country_short_code: detail.country_short_code,
        contact_phone_country_short_code: detail.contact_phone_country_short_code || detail.country_short_code,
        website: detail.website,
        business_type: detail.business_type,
        service_category: detail.category,
        description: detail.description,
        street: detail.street || '',
        city: detail.city || '',
        post_code: detail.post_code || '',
        country: detail.country,
        latitude: detail.latitude,
        longitude: detail.longitude,
        linked_harbor_ids: detail.linked_harbors?.length
          ? Array.from(new Set(detail.linked_harbors.map((harbor) => harbor.harbor_id)))
          : [],
        public_visibility: detail.public_visibility,
        image: detail.image,
        services: detail.services,
      });
    }
  }, [serviceProviderDetail, isEdit]);

  const countriesList = useMemo(
    () => localizedCountryOptions(currentLanguage.code || 'en'),
    [currentLanguage.code]
  );

  const validationSchema = useMemo(
    () => createServiceProviderValidationSchema(mode),
    [mode]
  );

  const validateField = useCallback(
    async (name: keyof ServiceProviderFormData, value: unknown) => {
      try {
        await validationSchema.validateAt(name, { ...formData, [name]: value });
        setErrors((prev) => ({ ...prev, [name]: '' }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => ({ ...prev, [name]: err.message }));
        }
      }
    },
    [formData, validationSchema]
  );

  const handleChange = useCallback(
    (
      field: keyof ServiceProviderFormData,
      value: string | number | string[] | undefined
    ) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      validateField(field, value);
    },
    [validateField]
  );

  useEffect(() => {
    const t = setTimeout(() => businessNameRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (mode === 'create' && !formData.country_short_code) {
      const finlandCountry = countriesList.find(
        (country) => country.code === 'FI'
      );
      if (finlandCountry) {
        const finlandData = countries.find(
          (c) => c.cca2 === finlandCountry.code
        );
        const countryCode = finlandData
          ? `${finlandData.idd.root || ''}${
              finlandData.idd.suffixes?.[0] || ''
            }`
          : '';

        setFormData((prev) => ({
          ...prev,
          country: englishCountryName('FI'),
          country_code: countryCode,
          country_short_code: finlandCountry.code,
          contact_phone_country_short_code: finlandCountry.code,
        }));
      }
    }
  }, [mode, formData.country_short_code, countriesList]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        harborDropdownRef.current &&
        !harborDropdownRef.current.contains(event.target as Node)
      ) {
        setIsHarborDropdownOpen(false);
      }
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCountryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredHarbors = useMemo(() => {
    return availableHarbors.filter((harbor) =>
      harbor.name.toLowerCase().includes(harborSearchTerm.toLowerCase())
    );
  }, [harborSearchTerm, availableHarbors]);

  const filteredCountries = useMemo(() => {
    const term = countrySearchTerm.toLowerCase();
    return countriesList.filter(
      (country) =>
        country.name.toLowerCase().includes(term) ||
        country.code.toLowerCase().includes(term)
    );
  }, [countrySearchTerm, countriesList]);

  const selectedHarborNames = useMemo(() => {
    const selectedIds = formData.linked_harbor_ids || [];
    return availableHarbors
      .filter((harbor) => selectedIds.includes(harbor.id))
      .map((harbor) => harbor.name);
  }, [formData.linked_harbor_ids, availableHarbors]);

  const handleHarborToggle = useCallback(
    (harborId: string) => {
      const currentIds = formData.linked_harbor_ids || [];
      const newIds = currentIds.includes(harborId)
        ? currentIds.filter((id) => id !== harborId)
        : [...currentIds, harborId];
      handleChange('linked_harbor_ids', newIds);
    },
    [formData.linked_harbor_ids, handleChange]
  );

  const handleRemoveHarbor = useCallback(
    (harborId: string) => {
      const currentIds = formData.linked_harbor_ids || [];
      const newIds = currentIds.filter((id) => id !== harborId);
      handleChange('linked_harbor_ids', newIds);
    },
    [formData.linked_harbor_ids, handleChange]
  );

  const [createServiceProvider] = useCreateServiceProviderMutation();
  const [updateServiceProvider] = useUpdateServiceProviderMutation();

  const handleSubmitForm = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      try {
        setIsSubmitting(true);
        await validationSchema.validate(formData, { abortEarly: false });

        const payload = {
          legal_name: formData.legal_name,
          display_name: formData.display_name,
          service_category: formData.service_category,
          vat_id: formData.vat_id,
          contact_email: formData.contact_email,
          contact_phone: normalizeNationalPhoneNumber(
            formData.contact_phone || '',
            formData.contact_phone_country_short_code || formData.country_short_code
          ),
          country_code:
            getDialCodeFromIso(
              formData.contact_phone_country_short_code,
              countryLabels
            ) || formData.country_code,
          country_short_code: formData.country_short_code,
          contact_phone_country_short_code: formData.contact_phone_country_short_code,
          website: formData.website,
          business_type: formData.business_type,
          description: formData.description,
          street: formData.street,
          city: formData.city,
          post_code: formData.post_code,
          country: formData.country,
          latitude:
            formData.latitude !== undefined && formData.latitude !== null
              ? Number(formData.latitude)
              : undefined,
          longitude:
            formData.longitude !== undefined && formData.longitude !== null
              ? Number(formData.longitude)
              : undefined,
          linked_harbor_ids: formData.linked_harbor_ids,
        };

        if (mode === 'create') {
          await createServiceProvider(payload).unwrap();
          enqueueSnackbar('Service Provider created successfully', {
            variant: 'success',
          });
        } else if (mode === 'edit' && formData.id) {
          await updateServiceProvider({
            id: formData.id,
            data: payload,
          }).unwrap();
          enqueueSnackbar('Service Provider updated successfully', {
            variant: 'success',
          });
        }

        const from =
          (location.state as { from?: string } | null)?.from ||
          '/service-management';
        navigate(from);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const validationErrors: { [key: string]: string } = {};
          err.inner.forEach((error) => {
            if (error.path) {
              validationErrors[error.path] = error.message;
            }
          });
          setErrors(validationErrors);
          return;
        }

        const apiError = err as ErrorApiResponse;
        enqueueSnackbar(
          apiError?.data?.message || 'Failed to save service provider',
          {
            variant: 'error',
          }
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      mode,
      formData,
      validationSchema,
      createServiceProvider,
      updateServiceProvider,
      enqueueSnackbar,
      navigate,
      location.state,
    ]
  );
  const isLoading = isDetailLoading && isEdit;

  const handleCancel = () => {
    const from =
      (location.state as { from?: string } | null)?.from ||
      '/service-management';
    navigate(from);
  };

  if (isLoading) {
    return (
      <Container>
        <div className="flex items-center justify-center h-64">
          <div className="loading loading-spinner loading-lg"></div>
          <span className="ml-2">Loading service provider...</span>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {mode === 'create'
                ? 'Add New Service Provider'
                : 'Edit Service Provider'}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {mode === 'create'
                ? 'Fill in the details to create a new service provider.'
                : 'Update the details for the service provider.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-sm btn-light flex items-center gap-2 border border-gray-300 rounded-lg px-3 sm:px-4 py-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={
                mode === 'create'
                  ? 'Cancel service provider creation'
                  : 'Cancel service provider editing'
              }
            >
              <KeenIcon type="filled" icon="cross" className="text-gray-500" />
              Cancel
            </button>
          </div>
        </div>

        <form
          onSubmit={handleSubmitForm}
          className="flex flex-col gap-4"
          noValidate
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
            <div style={{ minWidth: 0 }}>
              <label
                htmlFor="legal-name-input"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                Legal name
              </label>
              <input
                ref={businessNameRef}
                id="legal-name-input"
                name="legal_name"
                type="text"
                className={clsx(
                  'input input-bordered w-full break-words',
                  errors.legal_name && 'border-red-500 focus:ring-red-500'
                )}
                style={{ minWidth: 0, wordBreak: 'break-word' }}
                value={formData.legal_name || ''}
                onChange={(e) => handleChange('legal_name', e.target.value)}
                onKeyDown={preventLeadingWhitespace}
                disabled={isSubmitting || isLoadingServiceCategories}
                placeholder="Enter legal name"
                aria-invalid={!!errors.legal_name}
                aria-describedby={
                  errors.legal_name ? 'legal-name-error' : undefined
                }
                autoComplete="off"
              />
              {errors.legal_name && (
                <p id="legal-name-error" className="text-red-500 text-sm mt-1">
                  {errors.legal_name}
                </p>
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <label
                htmlFor="display-name-input"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                Display name
              </label>
              <input
                id="display-name-input"
                name="display_name"
                type="text"
                className={clsx(
                  'input input-bordered w-full break-words',
                  errors.display_name && 'border-red-500 focus:ring-red-500'
                )}
                style={{ minWidth: 0, wordBreak: 'break-word' }}
                value={formData.display_name || ''}
                onChange={(e) => handleChange('display_name', e.target.value)}
                onKeyDown={preventLeadingWhitespace}
                disabled={isSubmitting}
                placeholder="Enter display name"
                aria-invalid={!!errors.display_name}
                aria-describedby={
                  errors.display_name ? 'display-name-error' : undefined
                }
                autoComplete="off"
              />
              {errors.display_name && (
                <p
                  id="display-name-error"
                  className="text-red-500 text-sm mt-1"
                >
                  {errors.display_name}
                </p>
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <label
                htmlFor="service-category-select"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                Service Category
              </label>
              <select
                id="service-category-select"
                name="service_category"
                className={clsx(
                  'select select-bordered w-full',
                  errors.service_category && 'border-red-500 focus:ring-red-500'
                )}
                value={formData.service_category || ''}
                onChange={(e) =>
                  handleChange('service_category', e.target.value)
                }
                disabled={isSubmitting}
                aria-invalid={!!errors.service_category}
                aria-describedby={
                  errors.service_category ? 'service-category-error' : undefined
                }
              >
                <option value="">
                  {isLoadingServiceCategories
                    ? 'Loading categories...'
                    : 'Select a category'}
                </option>
                {serviceCategories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              {errors.service_category && (
                <p
                  id="service-category-error"
                  className="text-red-500 text-sm mt-1"
                >
                  {errors.service_category}
                </p>
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <label
                htmlFor="vat-id-input"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                VAT ID
              </label>
              <input
                id="vat-id-input"
                name="vat_id"
                type="text"
                className={clsx(
                  'input input-bordered w-full',
                  errors.vat_id && 'border-red-500 focus:ring-red-500'
                )}
                value={formData.vat_id || ''}
                onChange={(e) => handleChange('vat_id', e.target.value)}
                disabled={isSubmitting}
                placeholder="Enter VAT ID"
                aria-invalid={!!errors.vat_id}
                aria-describedby={errors.vat_id ? 'vat-id-error' : undefined}
                style={{ minWidth: 0 }}
                autoComplete="off"
              />
              {errors.vat_id && (
                <p id="vat-id-error" className="text-red-500 text-sm mt-1">
                  {errors.vat_id}
                </p>
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <label
                htmlFor="contact-email-input"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                Contact Email
              </label>
              <input
                id="contact-email-input"
                name="contact_email"
                type="email"
                className={clsx(
                  'input input-bordered w-full',
                  errors.contact_email && 'border-red-500 focus:ring-red-500'
                )}
                value={formData.contact_email || ''}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                disabled={isSubmitting}
                placeholder="example@domain.com"
                aria-invalid={!!errors.contact_email}
                aria-describedby={
                  errors.contact_email ? 'contact-email-error' : undefined
                }
                autoComplete="email"
              />
              {errors.contact_email && (
                <p
                  id="contact-email-error"
                  className="text-red-500 text-sm mt-1"
                >
                  {errors.contact_email}
                </p>
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <label
                htmlFor="contact_phone-input"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                Contact phone
              </label>
              <div className="flex gap-2" style={{ minWidth: 0 }}>
                <div
                  style={{ width: '100px', minWidth: '100px', flexShrink: 0 }}
                >
                  <CountryCodeSelect
                    selected={formData.contact_phone_country_short_code || formData.country_short_code || 'US'}
                    setFieldValue={(
                      dialCode: string,
                      countryShortCode: string
                    ) => {
                      setFormData((prev) => ({
                        ...prev,
                        contact_phone_country_short_code: countryShortCode,
                        country_code:
                          dialCode ||
                          getDialCodeFromIso(countryShortCode, countryLabels) ||
                          prev.country_code,
                      }));
                      validateField(
                        'contact_phone_country_short_code',
                        countryShortCode
                      );
                    }}
                    disabled={isSubmitting}
                    hasError={!!errors.contact_phone_country_short_code}
                  />
                </div>
                <div className="flex-1" style={{ minWidth: 0 }}>
                  <input
                    id="contact_phone-input"
                    name="contact_phone"
                    type="tel"
                    className={clsx(
                      'input input-bordered w-full',
                      errors.contact_phone &&
                        'border-red-500 focus:ring-red-500'
                    )}
                    value={formData.contact_phone || ''}
                    onChange={(e) =>
                      handleChange('contact_phone', e.target.value)
                    }
                    disabled={isSubmitting}
                    placeholder="1234567890"
                    aria-invalid={!!errors.contact_phone}
                    aria-describedby={
                      errors.contact_phone ? 'contact_phone-error' : undefined
                    }
                    style={{ minWidth: 0 }}
                    autoComplete="tel"
                  />
                  {errors.contact_phone && (
                    <p
                      id="contact_phone-error"
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.contact_phone}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="md:col-span-2" style={{ minWidth: 0 }}>
              <label
                htmlFor="description-input"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                Description
              </label>
              <textarea
                id="description-input"
                name="description"
                className={clsx(
                  'textarea textarea-bordered w-full',
                  errors.description && 'border-red-500 focus:ring-red-500'
                )}
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                disabled={isSubmitting}
                placeholder="Enter description"
                rows={4}
                aria-invalid={!!errors.description}
                aria-describedby={
                  errors.description ? 'description-error' : undefined
                }
                style={{ minWidth: 0, resize: 'vertical' }}
                autoComplete="off"
              />
              {errors.description && (
                <p id="description-error" className="text-red-500 text-sm mt-1">
                  {errors.description}
                </p>
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <label
                htmlFor="website-input"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                Website
              </label>
              <input
                id="website-input"
                name="website"
                type="url"
                className={clsx(
                  'input input-bordered w-full',
                  errors.website && 'border-red-500 focus:ring-red-500'
                )}
                value={formData.website || ''}
                onChange={(e) => handleChange('website', e.target.value)}
                disabled={isSubmitting}
                placeholder="https://example.com"
                aria-invalid={!!errors.website}
                aria-describedby={errors.website ? 'website-error' : undefined}
                style={{ minWidth: 0 }}
                autoComplete="off"
              />
              {errors.website && (
                <p id="website-error" className="text-red-500 text-sm mt-1">
                  {errors.website}
                </p>
              )}
            </div>

            <div className="md:col-span-2" style={{ minWidth: 0 }}>
              <label
                htmlFor="harbor-select"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                Link harbor
              </label>
              <div ref={harborDropdownRef} className="relative">
                <div
                  id="harbor-select"
                  role="combobox"
                  aria-expanded={isHarborDropdownOpen}
                  aria-haspopup="listbox"
                  aria-controls="harbor-listbox"
                  aria-label="Link harbor"
                  tabIndex={0}
                  className={clsx(
                    'input input-bordered w-full min-h-[55px] h-full cursor-pointer flex flex-wrap gap-2 items-center transition-all duration-200 hover:border-primary',
                    errors.linked_harbor_ids && 'border-red-500',
                    isHarborDropdownOpen && 'border-primary ring-2 ring-sky-200'
                  )}
                  onClick={() =>
                    !isSubmitting &&
                    setIsHarborDropdownOpen(!isHarborDropdownOpen)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (!isSubmitting) {
                        setIsHarborDropdownOpen(!isHarborDropdownOpen);
                      }
                    }
                  }}
                  style={{ minWidth: 0, padding: '8px' }}
                >
                  {selectedHarborNames.length > 0 ? (
                    selectedHarborNames.map((name) => {
                      const harbor = availableHarbors.find(
                        (h) => h.name === name
                      );
                      return (
                        <span
                          key={harbor?.id}
                          className="inline-flex items-center gap-1 bg-gradient-to-r from-sky-100 contrast:from-blue-950 contrast:to-blue-950 dark:from-blue-950 dark:to-blue-950 to-blue-100 text-sky-800 dark:text-sky-100 contrast:text-sky-100 px-3 py-1.5 rounded-full text-sm font-medium shadow-sm"
                        >
                          {name}
                          {!isSubmitting && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveHarbor(harbor?.id || '');
                              }}
                              className="hover:bg-sky-300 rounded-full p-0.5 transition-colors"
                              aria-label={`Remove ${name}`}
                            >
                              <KeenIcon icon="cross" className="text-xs" />
                            </button>
                          )}
                        </span>
                      );
                    })
                  ) : (
                    <span className="text-gray-600">
                      Select harbor
                    </span>
                  )}
                  {!isSubmitting && (
                    <KeenIcon
                      icon={isHarborDropdownOpen ? 'up' : 'down'}
                      className="ml-auto text-gray-400"
                    />
                  )}
                </div>

                {isHarborDropdownOpen && !isSubmitting && (
                  <div
                    className="absolute z-50 w-full mt-1 bg-gray-100 border border-sky-200 dark:border-gray-400 contrast:border-gray-400 rounded-lg shadow-2xl max-h-64 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                    style={{ minWidth: 0 }}
                  >
                    <div className="p-3 border-b border-sky-100 dark:border-gray-400 bg from-sky-50 dark:from-gray-200 dark:to-gray-200 to-blue-50">
                      <div className="relative">
                        <KeenIcon
                          icon="magnifier"
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                        <input
                          type="text"
                          placeholder="Search harbors..."
                          value={harborSearchTerm}
                          onChange={(e) => setHarborSearchTerm(e.target.value)}
                          className="input input-sm w-full pl-10 border-sky-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
                          onClick={(e) => e.stopPropagation()}
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    <div
                      id="harbor-listbox"
                      role="listbox"
                      className="overflow-y-auto max-h-48"
                      tabIndex={0}
                    >
                      {isLoadingHarbors ? (
                        <div className="px-4 py-2 text-gray-500 flex items-center">
                          <div className="loading loading-spinner loading-xs mr-2"></div>
                          Loading harbors...
                        </div>
                      ) : filteredHarbors.length > 0 ? (
                        filteredHarbors.map((harbor) => {
                          const isSelected = (
                            formData.linked_harbor_ids || []
                          ).includes(harbor.id);
                          return (
                            <div
                              key={harbor.id}
                              role="option"
                              aria-selected={isSelected}
                              className={clsx(
                                'px-4 py-2.5 cursor-pointer hover:bg-sky-50 dark:hover:bg-gray-200 contrast:hover:bg-gray-200 flex items-center gap-2 transition-colors',
                                isSelected &&
                                  'bg-gradient-to-r from-sky-100 dark:from-gray-300 contrast:from-gray-300 to-blue-100 dark:to-gray-300 contrast:to-gray-300 font-medium'
                              )}
                              onClick={() => handleHarborToggle(harbor.id)}
                            >
                              <label
                                htmlFor={`harbor-select-${harbor.id}`}
                                className="flex items-center gap-2 cursor-pointer w-full"
                              >
                                <input
                                  type="checkbox"
                                  id={`harbor-select-${harbor.id}`}
                                  checked={isSelected}
                                  onChange={() => {}}
                                  className="checkbox checkbox-sm cursor-pointer"
                                />
                                <span className="text-sm cursor-pointer">
                                  {harbor.name}
                                </span>
                              </label>
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                          No harbors found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {errors.linked_harbor_ids && (
                <p id="harbor-error" className="text-red-500 text-sm mt-1">
                  {errors.linked_harbor_ids}
                </p>
              )}
            </div>

            <div className="md:col-span-2" style={{ minWidth: 0 }}>
              <label
                htmlFor="street-input"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                Enter Street
              </label>
              <input
                id="street-input"
                name="street"
                type="text"
                className={clsx(
                  'input input-bordered w-full',
                  errors.street && 'border-red-500 focus:ring-red-500'
                )}
                value={formData.street || ''}
                onChange={(e) => handleChange('street', e.target.value)}
                disabled={isSubmitting}
                placeholder="Enter street"
                aria-invalid={!!errors.street}
                aria-describedby={errors.street ? 'street-error' : undefined}
                style={{ minWidth: 0 }}
                autoComplete="off"
              />
              {errors.street && (
                <p id="street-error" className="text-red-500 text-sm mt-1">
                  {errors.street}
                </p>
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <label
                htmlFor="city-input"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                City
              </label>
              <input
                id="city-input"
                name="city"
                type="text"
                className={clsx(
                  'input input-bordered w-full',
                  errors.city && 'border-red-500 focus:ring-red-500'
                )}
                value={formData.city || ''}
                onChange={(e) => handleChange('city', e.target.value)}
                disabled={isSubmitting}
                placeholder="Enter city"
                aria-invalid={!!errors.city}
                aria-describedby={errors.city ? 'city-error' : undefined}
                style={{ minWidth: 0 }}
                autoComplete="off"
              />
              {errors.city && (
                <p id="city-error" className="text-red-500 text-sm mt-1">
                  {errors.city}
                </p>
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <label
                htmlFor="post-code-input"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                Post code
              </label>
              <input
                id="post-code-input"
                name="post_code"
                type="text"
                className={clsx(
                  'input input-bordered w-full',
                  errors.post_code && 'border-red-500 focus:ring-red-500'
                )}
                value={formData.post_code || ''}
                onChange={(e) => handleChange('post_code', e.target.value)}
                disabled={isSubmitting}
                placeholder="Enter post code"
                aria-invalid={!!errors.post_code}
                aria-describedby={
                  errors.post_code ? 'post-code-error' : undefined
                }
                style={{ minWidth: 0 }}
                autoComplete="off"
              />
              {errors.post_code && (
                <p id="post-code-error" className="text-red-500 text-sm mt-1">
                  {errors.post_code}
                </p>
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <label
                htmlFor="country-input"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                Country
              </label>
              <div ref={countryDropdownRef} className="relative">
                <div
                  id="country-input"
                  role="combobox"
                  aria-expanded={isCountryDropdownOpen}
                  aria-haspopup="listbox"
                  aria-controls="country-listbox"
                  aria-label="Country"
                  tabIndex={0}
                  className={clsx(
                    'input input-bordered w-full h-11 flex items-center justify-between cursor-pointer',
                    errors.country && 'border-red-500 focus:ring-red-500'
                  )}
                  onClick={() =>
                    !isSubmitting &&
                    setIsCountryDropdownOpen(!isCountryDropdownOpen)
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (!isSubmitting) {
                        setIsCountryDropdownOpen(!isCountryDropdownOpen);
                      }
                    }
                  }}
                >
                  <span className="truncate">
                    {countriesList.find(
                      (c) => c.code === formData.country_short_code
                    )?.name ||
                      formData.country ||
                      'Select country'}
                  </span>
                  {!isSubmitting && (
                    <KeenIcon
                      icon={isCountryDropdownOpen ? 'up' : 'down'}
                      className="text-gray-400"
                    />
                  )}
                </div>

                {isCountryDropdownOpen && !isSubmitting && (
                  <div className="absolute z-50 w-full mt-1 bg-gray-100 border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
                    <div className="p-2 border-b border-gray-200 bg-gray-200">
                      <div className="relative">
                        <KeenIcon
                          icon="magnifier"
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                        <input
                          type="text"
                          placeholder="Search countries..."
                          value={countrySearchTerm}
                          onChange={(e) => setCountrySearchTerm(e.target.value)}
                          className="input input-sm w-full pl-10 border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                          onClick={(e) => e.stopPropagation()}
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    <div
                      id="country-listbox"
                      role="listbox"
                      className="overflow-y-auto max-h-48"
                      tabIndex={0}
                    >
                      {filteredCountries.length > 0 ? (
                        filteredCountries.map((country) => (
                          <div
                            key={country.code}
                            role="option"
                            aria-selected={
                              formData.country_short_code === country.code
                            }
                            className="px-4 py-2.5 cursor-pointer hover:bg-gray-200 flex items-center gap-2"
                            onClick={() => {
                              const countryData = countries.find(
                                (c) => c.cca2 === country.code
                              );
                              const countryCode = countryData
                                ? `${countryData.idd.root || ''}${
                                    countryData.idd.suffixes?.[0] || ''
                                  }`
                                : '';

                              setFormData((prev) => {
                                const phoneDialEmpty = !prev.country_code;
                                const phoneIsoEmpty =
                                  !prev.contact_phone_country_short_code;
                                return {
                                  ...prev,
                                  country:
                                    countryData?.name.common || country.name,
                                  country_short_code: country.code,
                                  ...(phoneDialEmpty && countryCode
                                    ? { country_code: countryCode }
                                    : {}),
                                  ...(phoneDialEmpty && phoneIsoEmpty
                                    ? {
                                        contact_phone_country_short_code:
                                          country.code,
                                      }
                                    : {}),
                                };
                              });
                              setIsCountryDropdownOpen(false);
                              setCountrySearchTerm('');
                            }}
                          >
                            <span className="text-sm">{country.name}</span>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                          No countries found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {errors.country && (
                <p id="country-error" className="text-red-500 text-sm mt-1">
                  {errors.country}
                </p>
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <label
                htmlFor="latitude-input"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                Latitude
              </label>
              <NumberInput
                id="latitude-input"
                name="latitude"
                allowNegative
                step="0.0001"
                min="-90"
                max="90"
                maxDecimals={100}
                className={clsx(
                  'input input-bordered w-full',
                  errors.latitude && 'border-red-500 focus:ring-red-500'
                )}
                value={
                  formData.latitude !== undefined && formData.latitude !== null
                    ? String(formData.latitude)
                    : ''
                }
                onChange={(e) =>
                  handleChange(
                    'latitude',
                    e.target.value !== '' ? Number(e.target.value) : undefined
                  )
                }
                disabled={isSubmitting}
                placeholder="Enter latitude"
                aria-invalid={!!errors.latitude}
                aria-describedby={
                  errors.latitude ? 'latitude-error' : undefined
                }
                style={{ minWidth: 0 }}
                autoComplete="off"
              />
              {errors.latitude && (
                <p id="latitude-error" className="text-red-500 text-sm mt-1">
                  {errors.latitude}
                </p>
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <label
                htmlFor="longitude-input"
                className="block text-sm font-medium text-gray-700 mb-1 break-words"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                Longitude
              </label>
              <NumberInput
                id="longitude-input"
                name="longitude"
                allowNegative
                step="0.0001"
                min="-180"
                max="180"
                maxDecimals={100}
                className={clsx(
                  'input input-bordered w-full',
                  errors.longitude && 'border-red-500 focus:ring-red-500'
                )}
                value={
                  formData.longitude !== undefined &&
                  formData.longitude !== null
                    ? String(formData.longitude)
                    : ''
                }
                onChange={(e) =>
                  handleChange(
                    'longitude',
                    e.target.value !== '' ? Number(e.target.value) : undefined
                  )
                }
                disabled={isSubmitting}
                placeholder="Enter longitude"
                aria-invalid={!!errors.longitude}
                aria-describedby={
                  errors.longitude ? 'longitude-error' : undefined
                }
                style={{ minWidth: 0 }}
                autoComplete="off"
              />
              {errors.longitude && (
                <p id="longitude-error" className="text-red-500 text-sm mt-1">
                  {errors.longitude}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? mode === 'create'
                  ? 'Creating...'
                  : 'Saving...'
                : mode === 'create'
                  ? 'Create Service Provider'
                  : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default ServiceProviderFormPage;
